import { ParsedUrlQuery } from 'querystring';

import { memo, useMemo, useState } from 'react';

import { useSearchParams } from 'next/navigation';
import dynamic from 'next/dynamic';

import FiltersBlock, {
  EntityFilter,
} from '@components/ui/filters/FiltersBlock';
import { FlexibleDiv } from '@components/formStyles';
import { isEmptyFilters, paramsToObject } from '@components/views/utils';
import ReviewFilter from '@components/ui/filters/ReviewFilter/ReviewFilter';
import RegionFilter from '@components/views/components/RegionFilterWithSearchBar';
import ExpediaReviewFilter from '@components/ui/filters/ExpediaReviewFilter';

import {
  StyledSingleSelectionFilterWrapper,
  StyledMultiSelectionFilterWrapper,
  StyledPriceRangeFilterWrapper,
  StyledNameFilterWrapper,
  StyledHotelClassFilter,
  StyledExpediaPointFilterWrapper,
} from './ListingFiltersStyle';

const PriceRangeFilter = dynamic(
  () => import('@components/ui/filters/PriceRangeFilter/PriceRangeFilter')
);
const NameFilter = dynamic(
  () => import('@components/ui/filters/NameFilter/NameFilter')
);
const SingleSelectionFilter = dynamic(
  () =>
    import('@components/ui/filters/SingleSelectionFilter/SingleSelectionFilter')
);
const CollapsibleMultiSelectionFilter = dynamic(
  () =>
    import(
      '@components/ui/filters/MultiSelectionFilter/CollapsibleMultiSelectionFilter'
    )
);

const ClearAllFilters = dynamic(
  () => import('@components/views/components/ClearAllFilters/ClearAllFilters')
);
const ClassFilter = dynamic(
  () => import('@components/ui/filters/ClassFilter/ClassFilter')
);

const CollapsibleMultiSelectionFilterWithSearchBar = dynamic(
  () =>
    import(
      '@components/ui/filters/MultiSelectionFilterWithSearchBar/CollapsibleMultiSelectionFilter'
    )
);

type Props = {
  hotelFilters: EntityFilter[] | undefined;
  isExpediaHotels: boolean;
  hotelsCount?: number;
  isHotelsLoading?: boolean;
  filterRegionText?: string;
  isError?: boolean;
  setIsRedirecting?: (value: boolean) => void;
  isCity?: boolean;
};

const ListingFilters = ({
  hotelFilters,
  isExpediaHotels,
  hotelsCount,
  isHotelsLoading,
  filterRegionText,
  isError,
  setIsRedirecting,
  isCity = false,
}: Props) => {
  const searchParams = useSearchParams();
  const [checkedKeys, setCheckedKeys] = useState<React.Key[]>();
  const query: ParsedUrlQuery = useMemo(() => {
    return paramsToObject(searchParams?.entries());
  }, [searchParams]);

  const pointFilter = useMemo(() => {
    return hotelFilters?.filter((item) => {
      return item?.type === 'point';
    });
  }, [hotelFilters]);

  const featureFilter = useMemo(() => {
    return hotelFilters?.filter((item) => {
      return item?.type === 'feature';
    });
  }, [hotelFilters]);

  const featureFilterIsOpened = (item, selectedFilters) => {
    //Feature Filter should be opened or not
    if (item?.items?.length) {
      const allItems = item.items.map((item) => item.key);
      const existsInAllItems = selectedFilters.some((filter) =>
        allItems.includes(filter)
      );
      return existsInAllItems;
    }
    return false;
  };
  return (
    <>
      <FiltersBlock
        applicableFilters={hotelFilters}
        setIsRedirecting={setIsRedirecting}
        setCheckedKeys={setCheckedKeys}
      >
        {({
          filters,
          onRegionChecked: onCheck,
          onlyAvailableHotels,
          handleSingleSelectionFilter,
          handleSelectedPopularities,
          freeCancellation,
          popularities,
          keywords,
          setKeywords,
          handleKeywordsFilter,
          handleClearKeywordsFilter,
          minAccPriceValue,
          maxAccPriceValue,
          setMaxAccPriceValue,
          setMinAccPriceValue,
          handlePriceFilter,
          boardTypes,
          themes,
          handleSelectedThemes,
          handleSelectedAccTypes,
          handleClearAllFilters,
          handleStarFilter,
          handleSelectedTripAdvisor,
          categories,
          handleSelectedCategories,
          regions,
          handleSelectedFeatures,
        }) => {
          return (
            <>
              <FlexibleDiv bottom={20}>
                <ClearAllFilters
                  onClick={handleClearAllFilters}
                  disabled={isEmptyFilters(query)}
                />
              </FlexibleDiv>

              {isExpediaHotels ? (
                <>
                  <StyledHotelClassFilter>
                    <ClassFilter
                      title='Otel Sınıfı'
                      handleSearch={handleStarFilter}
                    />
                  </StyledHotelClassFilter>

                  {freeCancellation?.count ? (
                    <StyledSingleSelectionFilterWrapper>
                      <SingleSelectionFilter
                        title={freeCancellation?.value || 'İptal Politikası'}
                        subTitle={freeCancellation?.count || ''}
                        checked={!!filters.freeCancellation}
                        name='refundable'
                        mode='controlled'
                        handleCheckBoxChange={(e) =>
                          handleSingleSelectionFilter(e, 'refundable')
                        }
                      />
                    </StyledSingleSelectionFilterWrapper>
                  ) : null}

                  <StyledNameFilterWrapper>
                    <NameFilter
                      inputWrapperClassName='listing-hotel-name-filter'
                      title='Otel Adı'
                      name={keywords}
                      setName={setKeywords}
                      placeholder='Otel adı ile arayın'
                      handleSearch={handleKeywordsFilter}
                      handleClearKeywordsFilter={handleClearKeywordsFilter}
                      withClear
                    />
                  </StyledNameFilterWrapper>

                  {pointFilter?.[0]?.items?.length ? (
                    <StyledExpediaPointFilterWrapper>
                      <ExpediaReviewFilter
                        title='Misafir Puanı'
                        point={pointFilter}
                        handleSelectedItems={handleSelectedTripAdvisor}
                        selectedItems={query?.point || ''}
                      />
                    </StyledExpediaPointFilterWrapper>
                  ) : null}
                  <StyledPriceRangeFilterWrapper>
                    <PriceRangeFilter
                      minValue={minAccPriceValue}
                      maxValue={maxAccPriceValue}
                      setMinValue={setMinAccPriceValue}
                      setMaxValue={setMaxAccPriceValue}
                      title='Konaklama Fiyatı'
                      handleSearch={handlePriceFilter}
                    />
                  </StyledPriceRangeFilterWrapper>

                  {!boardTypes || boardTypes.length === 0 ? null : (
                    <StyledMultiSelectionFilterWrapper>
                      <CollapsibleMultiSelectionFilter
                        title='Otel Konaklama Tipi'
                        selectedItems={
                          filters.selectedAccommodationTypes?.map(
                            (item) => item.key
                          ) ?? []
                        }
                        data={boardTypes}
                        subTitleKey='count'
                        handleSelectedItems={handleSelectedAccTypes}
                      />
                    </StyledMultiSelectionFilterWrapper>
                  )}
                </>
              ) : (
                <>
                  <FlexibleDiv bottom={20}>
                    <RegionFilter
                      totalItemCount={hotelsCount ?? 0}
                      isLoading={isHotelsLoading ?? false}
                      title={filterRegionText || ''}
                      regions={regions ?? []}
                      onCheck={(checkedKeysValue) => {
                        setCheckedKeys(checkedKeysValue);
                        return onCheck(checkedKeysValue);
                      }}
                      selectedRegions={
                        filters.selectedRegions
                          ? filters.selectedRegions
                          : checkedKeys
                      }
                      isError={isError ?? false}
                      isCity={isCity}
                    />
                  </FlexibleDiv>

                  {onlyAvailableHotels?.count ? (
                    <StyledSingleSelectionFilterWrapper>
                      <SingleSelectionFilter
                        title={
                          onlyAvailableHotels?.value || 'Sadece Müsait Tesisler'
                        }
                        subTitle={onlyAvailableHotels?.count || undefined}
                        checked={!!filters.onlyAvailableProperties}
                        mode='controlled'
                        handleCheckBoxChange={(e) =>
                          handleSingleSelectionFilter(e, 'status')
                        }
                        name='status'
                      />
                    </StyledSingleSelectionFilterWrapper>
                  ) : null}

                  {freeCancellation?.count ? (
                    <StyledSingleSelectionFilterWrapper>
                      <SingleSelectionFilter
                        title={freeCancellation?.value || 'Ücretsiz İptal'}
                        subTitle={freeCancellation?.count || ''}
                        checked={!!filters.freeCancellation}
                        mode='controlled'
                        handleCheckBoxChange={(e) =>
                          handleSingleSelectionFilter(e, 'refundable')
                        }
                        name='refundable'
                      />
                    </StyledSingleSelectionFilterWrapper>
                  ) : null}

                  <StyledNameFilterWrapper>
                    <NameFilter
                      inputWrapperClassName='listing-hotel-name-filter'
                      title='Otel Adı'
                      name={keywords}
                      setName={setKeywords}
                      placeholder='Otel adı ile arayın'
                      handleSearch={handleKeywordsFilter}
                      handleClearKeywordsFilter={handleClearKeywordsFilter}
                      withClear
                    />
                  </StyledNameFilterWrapper>
                  {pointFilter?.[0]?.items?.length ? (
                    <StyledNameFilterWrapper>
                      <ReviewFilter
                        title='Misafir Puanı'
                        point={pointFilter}
                        handleSelectedItems={handleSelectedTripAdvisor}
                        selectedItems={query?.point || ''}
                      />
                    </StyledNameFilterWrapper>
                  ) : null}

                  <StyledPriceRangeFilterWrapper>
                    <PriceRangeFilter
                      minValue={minAccPriceValue}
                      maxValue={maxAccPriceValue}
                      setMinValue={setMinAccPriceValue}
                      setMaxValue={setMaxAccPriceValue}
                      title='Konaklama Fiyatı'
                      handleSearch={handlePriceFilter}
                    />
                  </StyledPriceRangeFilterWrapper>

                  {!boardTypes || boardTypes.length === 0 ? null : (
                    <StyledMultiSelectionFilterWrapper>
                      <CollapsibleMultiSelectionFilter
                        title='Otel Konaklama Tipi'
                        selectedItems={
                          filters.selectedAccommodationTypes?.map(
                            (item) => item.key
                          ) ?? []
                        }
                        data={boardTypes}
                        subTitleKey='count'
                        handleSelectedItems={handleSelectedAccTypes}
                      />
                    </StyledMultiSelectionFilterWrapper>
                  )}

                  {themes?.length === 0 ? null : (
                    <StyledMultiSelectionFilterWrapper>
                      <CollapsibleMultiSelectionFilterWithSearchBar
                        title='Otel Temaları'
                        data={themes || []}
                        subTitleKey='count'
                        selectedItems={
                          filters.selectedThemeItems?.map((item) => item.key) ??
                          []
                        }
                        handleSelectedItems={handleSelectedThemes}
                        singleItemClassName='listing-theme-item-filter'
                      />
                    </StyledMultiSelectionFilterWrapper>
                  )}

                  {featureFilter?.map((item, index) =>
                    item.items.length === 0 ? null : (
                      <StyledMultiSelectionFilterWrapper key={index}>
                        <CollapsibleMultiSelectionFilterWithSearchBar
                          title={item.title}
                          data={item.items || []}
                          subTitleKey='count'
                          selectedItems={
                            filters.selectedFeaturesItems?.map(
                              (item) => item.key
                            ) ?? []
                          }
                          handleSelectedItems={handleSelectedFeatures}
                          openTranferFilter={featureFilterIsOpened(
                            item,
                            filters.selectedFeaturesItems?.map(
                              (item) => item.key
                            ) ?? []
                          )}
                        />
                      </StyledMultiSelectionFilterWrapper>
                    )
                  )}

                  {popularities?.length === 0 ? null : (
                    <StyledMultiSelectionFilterWrapper>
                      <CollapsibleMultiSelectionFilterWithSearchBar
                        title='Otel Özellikleri'
                        data={popularities || []}
                        subTitleKey='count'
                        selectedItems={
                          filters.selectedPopularitiesItems?.map(
                            (item) => item.key
                          ) ?? []
                        }
                        handleSelectedItems={handleSelectedPopularities}
                        singleItemClassName='listing-pupularities-item-filter'
                      />
                    </StyledMultiSelectionFilterWrapper>
                  )}

                  {categories?.length === 0 ? null : (
                    <StyledMultiSelectionFilterWrapper>
                      <CollapsibleMultiSelectionFilterWithSearchBar
                        title='Otel Tipi'
                        data={categories || []}
                        subTitleKey='count'
                        selectedItems={
                          filters.selectedCategoryItems?.map(
                            (item) => item.key
                          ) ?? []
                        }
                        handleSelectedItems={handleSelectedCategories}
                        singleItemClassName='listing-category-item-filter'
                      />
                    </StyledMultiSelectionFilterWrapper>
                  )}
                </>
              )}
            </>
          );
        }}
      </FiltersBlock>
    </>
  );
};

export default memo(ListingFilters);
